import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';

const CatalogPage = ({ sess = { uid: '', role: 'user' } }) => {
  const { showSuccess, showError } = useUIStore();
  const { confirm } = useConfirm();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('/chatbot/api/v1.0/catalog/list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: sess.uid }),
      });
      const result = await response.json();
      if (result?.data) {
        setProducts(result.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    const isConfirmed = await confirm({
      title: 'Delete Product',
      message: 'Are you sure you want to delete this product?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;
    try {
      const response = await fetch('/chatbot/api/v1.0/catalog/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: sess.uid, productId }),
      });
      const result = await response.json();
      if (response.ok) {
        showSuccess(result.message || 'Product deleted');
        fetchProducts();
      } else {
        showError(result.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showError('Error deleting product');
    }
  };

  const filteredProducts = products.filter((prod) =>
    prod.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/">
            Back
          </Button>
          <Typography variant="h5">Catalog</Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          href="/catalog/create"
        >
          Add Product
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ maxWidth: 320 }}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography align="center" color="text.secondary">
                          Loading...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography align="center" color="text.secondary">
                          No products found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts.map((prod, index) => (
                      <TableRow key={prod.id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{prod.name}</TableCell>
                        <TableCell>{prod.category || '-'}</TableCell>
                        <TableCell>{prod.price}</TableCell>
                        <TableCell>{prod.stock}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <IconButton size="small" color="primary" href={`/catalog/${prod.id}`}>
                              <Pencil size={16} />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(prod.id)}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CatalogPage;
